import React, { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useAtomValue } from 'jotai'
import { ChevronLeft, HelpCircle } from 'lucide-react'
import Sidebar from '../../components/Sidebar/Sidebar'
import { drumtaleAdminSessionAtom, drumtaleAdminTokenAtom } from '../../AtomStore'
import { gradeAccessPolicy, openPageSpecification } from '../shared/pageSpecification'
import {
  Member,
  membersDataModelGuide,
  membersImplementationGuide,
  membersSpecification,
  membersStateDefinitions,
} from './membersData'
import './Members.scss'

const TOAST_DURATION_MS = 2400

function MemberDetail() {
  const navigate = useNavigate()
  const { name } = useParams<{ name: string }>()
  const location = useLocation()

  const isAdminSessionActive = useAtomValue(drumtaleAdminSessionAtom)
  const adminToken = useAtomValue(drumtaleAdminTokenAtom)

  const [toastMessage, setToastMessage] = useState('')
  const [isToastVisible, setIsToastVisible] = useState(false)
  const toastTimerRef = useRef<number | null>(null)

  const [member, setMember] = useState<Member | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const showToast = (message: string) => {
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current)
    setToastMessage(message)
    setIsToastVisible(true)
    toastTimerRef.current = window.setTimeout(() => setIsToastVisible(false), TOAST_DURATION_MS)
  }

  useEffect(() => {
    if (!isAdminSessionActive) navigate('/')
  }, [isAdminSessionActive, navigate])

  useEffect(() => {
    const stateMember = (location.state as { member?: Member } | null)?.member
    if (stateMember) {
      setMember(stateMember)
      return
    }

    if (!isAdminSessionActive || !adminToken || !name) return

    const loadMembers = async () => {
      setIsLoading(true)
      try {
        const MainURL = (await import('../../MainURL')).default
        const response = await fetch(`${MainURL}/admin/members`, {
          headers: { Authorization: `Bearer ${adminToken}` },
        })
        const data = await response.json().catch(() => null)
        if (!response.ok) {
          showToast(data?.message || '회원 상세를 불러오지 못했습니다.')
          return
        }
        const decodedName = decodeURIComponent(name)
        const found = (data?.members ?? []).find((item: Member) => item.name === decodedName) ?? null
        if (!found) showToast('해당 회원을 찾을 수 없습니다.')
        setMember(found)
      } catch (error) {
        console.error(error)
        showToast('회원 상세를 불러오지 못했습니다.')
      } finally {
        setIsLoading(false)
      }
    }

    loadMembers()
  }, [adminToken, isAdminSessionActive, location.state, name])

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current)
    }
  }, [])

  const handleOpenSpecification = () => {
    const opened = openPageSpecification(membersSpecification, membersDataModelGuide, membersImplementationGuide, {
      gradeAccessPolicy,
      stateDefinitions: membersStateDefinitions,
    })
    if (!opened) showToast('화면 사양서 창을 열 수 없습니다. 팝업 차단을 해제해 주세요.')
  }

  const renderBody = () => {
    if (isLoading) {
      return <section className='panel'><p>불러오는 중...</p></section>
    }

    if (!member) {
      return <section className='panel'><p>해당 회원 정보를 찾을 수 없습니다.</p></section>
    }

    return (
      <div className='detail-stack'>
        <section className='detail-hero'>
          <div>
            <h2>{member.name} 회원 상세</h2>
          </div>
        </section>

        <div className='member-summary-grid'>
          <section className='panel'>
            <h3>가입 정보</h3>
            <div className='info-list'>
              <span>Google 이메일</span>
              <strong>{member.kakao}</strong>
              <span>가입 방식</span>
              <strong>Google</strong>
              <span>OS 유형</span>
              <strong>{member.joinType}</strong>
              <span>가입날짜</span>
              <strong>{member.joined}</strong>
              <span>회원 등급</span>
              <strong>{member.grade}</strong>
            </div>
          </section>

          <section className='panel permission-status-card'>
            <h3>권한 상태</h3>
            <div className='permission-checks'>
              <span className={`badge ${member.status === '구독 중' ? 'success' : 'warning'}`}>
                {member.status}
              </span>
              <span className='badge primary'>{`드럼 ${member.drum}회`}</span>
              <span className='badge success'>{`인지 ${member.cognitive}회`}</span>
            </div>
            <div className='card-actions'>
              <button
                className='secondary-button'
                type='button'
                onClick={() => showToast('구독 상세 보기 기능은 추후 제공됩니다.')}
              >
                구독 상세 보기
              </button>
            </div>
          </section>
        </div>

        <div className='member-history-grid'>
          <section className='panel history-open-card'>
            <div>
              <h3>Raw 로그</h3>
              <p>드럼 연주, 자동 연주, MIDI 입력 상태 전체 이력을 새 창에서 확인합니다.</p>
            </div>
            <button
              className='secondary-button'
              type='button'
              onClick={() => showToast('Raw 로그 전체 보기 기능은 추후 제공됩니다.')}
            >
              Raw 로그 전체 보기
            </button>
          </section>

          <section className='panel history-open-card'>
            <div>
              <h3>인지 리포트 이력</h3>
              <p>콘텐츠별 정답률, 반응속도, 생성일 전체 이력을 새 창에서 확인합니다.</p>
            </div>
            <button
              className='secondary-button'
              type='button'
              onClick={() => showToast('인지 리포트 전체 보기 기능은 추후 제공됩니다.')}
            >
              인지 리포트 전체 보기
            </button>
          </section>
        </div>
      </div>
    )
  }

  return (
    <div className='Members'>
      <div className='app-shell'>
        <Sidebar />
        <main className='workspace'>
          <header className='topbar'>
            <div className='title-area'>
              <button className='back-button' type='button' onClick={() => navigate('/admin/members')}>
                <ChevronLeft size={16} />
                뒤로가기
              </button>
              <h1>회원 상세</h1>
            </div>
          </header>

          <section className='view active'>
            {renderBody()}
          </section>
        </main>
      </div>

      <button
        className='page-spec-button'
        type='button'
        aria-label='현재 화면 사양서 열기'
        title='현재 화면 사양서'
        onClick={handleOpenSpecification}
      >
        <HelpCircle size={22} />
      </button>

      <div className={`toast${isToastVisible ? ' show' : ''}`} role='status' aria-live='polite'>
        {toastMessage}
      </div>
    </div>
  )
}

export default MemberDetail
